import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { calculateExamResults, getStudyRecommendations } from '../utils/examUtils';

const MockExamResults = ({ questions, selectedAnswers, onRetakeExam }) => {
  const results = calculateExamResults(questions, selectedAnswers);
  const studyRecommendations = getStudyRecommendations(results.questionResults);
  const [questionFilter, setQuestionFilter] = useState('all');

  const getPassFailIcon = (passed) => {
    return passed ? '✅' : '❌';
  };

  const getPassFailText = (passed) => {
    return passed ? 'PASSED' : 'FAILED';
  };

  const getPassFailColor = (passed) => {
    return passed ? 'text-green-600' : 'text-red-600';
  };

  const getOverallResultMessage = () => {
    if (results.examPassed) {
      return {
        icon: '🎉',
        title: 'Congratulations! You PASSED!',
        message: 'You have successfully met all the requirements for the Radio Amateur Examination.',
        color: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    } else {
      const failures = [];
      if (!results.scores.regulations.passed) failures.push('Regulations');
      if (!results.scores.technical.passed) failures.push('Technical');
      if (!results.scores.overall.passed) failures.push('Overall Average');
      
      return {
        icon: '📚',
        title: 'You did not pass this time',
        message: `You need to improve in: ${failures.join(', ')}. Review the recommended study areas below.`,
        color: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    }
  };

  const overallResult = getOverallResultMessage();

  // Filter questions based on selected filter
  const getFilteredQuestions = () => {
    switch (questionFilter) {
      case 'incorrect':
        return results.questionResults.filter(q => !q.isCorrect);
      case 'regulations':
        return results.questionResults.filter(q => q.isRegulation);
      case 'technical':
        return results.questionResults.filter(q => !q.isRegulation);
      default:
        return results.questionResults;
    }
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Overall Results Header */}
      <div className={`p-4 sm:p-6 md:p-8 ${overallResult.bgColor} border ${overallResult.borderColor} rounded-t-lg`}>
        <div className="text-center">
          <div className="text-4xl sm:text-5xl md:text-6xl mb-3 sm:mb-4">{overallResult.icon}</div>
          <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 ${overallResult.color} leading-tight`}>
            {overallResult.title}
          </h2>
          <p className={`text-base sm:text-lg ${overallResult.color} leading-relaxed px-2 sm:px-4`}>
            {overallResult.message}
          </p>
        </div>
      </div>

      {/* Detailed Scores */}
      <div className="p-4 sm:p-6 md:p-8 border-b border-gray-200">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Detailed Results</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
          {/* Regulations Score */}
          <div className="bg-gray-50 rounded-lg p-4 sm:p-5 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Regulations</h4>
              <span className="text-xl sm:text-2xl">{getPassFailIcon(results.scores.regulations.passed)}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {results.scores.regulations.correct}/40
            </div>
            <div className="text-base sm:text-lg text-gray-600 mb-2">
              {results.scores.regulations.percentage}%
            </div>
            <div className={`text-xs sm:text-sm font-medium ${getPassFailColor(results.scores.regulations.passed)}`}>
              {getPassFailText(results.scores.regulations.passed)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (Minimum: 20/40 or 50%)
            </div>
          </div>

          {/* Technical Score */}
          <div className="bg-gray-50 rounded-lg p-4 sm:p-5 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Technical</h4>
              <span className="text-xl sm:text-2xl">{getPassFailIcon(results.scores.technical.passed)}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {results.scores.technical.correct}/60
            </div>
            <div className="text-base sm:text-lg text-gray-600 mb-2">
              {results.scores.technical.percentage}%
            </div>
            <div className={`text-xs sm:text-sm font-medium ${getPassFailColor(results.scores.technical.passed)}`}>
              {getPassFailText(results.scores.technical.passed)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (Minimum: 30/60 or 50%)
            </div>
          </div>

          {/* Overall Score */}
          <div className="bg-gray-50 rounded-lg p-4 sm:p-5 md:p-6 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Overall</h4>
              <span className="text-xl sm:text-2xl">{getPassFailIcon(results.scores.overall.passed)}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {results.scores.overall.correct}/100
            </div>
            <div className="text-base sm:text-lg text-gray-600 mb-2">
              {results.scores.overall.percentage}%
            </div>
            <div className={`text-xs sm:text-sm font-medium ${getPassFailColor(results.scores.overall.passed)}`}>
              {getPassFailText(results.scores.overall.passed)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (Minimum: 65/100 or 65%)
            </div>
          </div>
        </div>
      </div>

      {/* Study Recommendations */}
      {studyRecommendations.length > 0 && (
        <div className="p-4 sm:p-6 md:p-8 border-b border-gray-200">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
            Study Recommendations
          </h3>
          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
            Based on your incorrect answers, we recommend reviewing these topics:
          </p>
          
          <div className="space-y-3 sm:space-y-4">
            {studyRecommendations.map((recommendation, index) => (
              <div key={recommendation.sourceId || `recommendation-${index}`} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between space-y-3 sm:space-y-0 sm:space-x-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-yellow-900 mb-1 text-sm sm:text-base break-words">
                      {recommendation.sourceTitle}
                    </h4>
                    <p className="text-yellow-800 text-xs sm:text-sm mb-2">
                      {recommendation.wrongQuestions.length} question{recommendation.wrongQuestions.length !== 1 ? 's' : ''} incorrect
                    </p>
                    <div className="text-yellow-700 text-xs break-words">
                      Questions: {recommendation.wrongQuestions.map(q => `#${q.examQuestionNumber}`).join(', ')}
                    </div>
                  </div>
                  <Link
                    to={`/${recommendation.sourceId.startsWith('regulation-') ? 'regulation' : 'chapter'}/${recommendation.sourceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto flex-shrink-0 bg-yellow-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded text-sm text-center hover:bg-yellow-700 active:bg-yellow-800 transition-colors min-h-[40px] sm:min-h-0 flex items-center justify-center touch-manipulation"
                  >
                    Review ↗
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Question-by-Question Review */}
      <div className="p-4 sm:p-6 md:p-8 border-b border-gray-200">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
          Question Review
        </h3>
        
        {/* Filter buttons */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-2 mb-6">
          <button 
            onClick={() => setQuestionFilter('all')}
            className={`px-3 sm:px-4 py-3 sm:py-2 rounded text-xs sm:text-sm transition-colors min-h-[44px] sm:min-h-0 touch-manipulation ${
              questionFilter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300'
            }`}
          >
            <span className="block sm:inline">All</span>
            <span className="block sm:inline sm:ml-1">({results.questionResults.length})</span>
          </button>
          <button 
            onClick={() => setQuestionFilter('incorrect')}
            className={`px-3 sm:px-4 py-3 sm:py-2 rounded text-xs sm:text-sm transition-colors min-h-[44px] sm:min-h-0 touch-manipulation ${
              questionFilter === 'incorrect' 
                ? 'bg-red-600 text-white' 
                : 'bg-red-100 text-red-700 hover:bg-red-200 active:bg-red-300'
            }`}
          >
            <span className="block sm:inline">Incorrect</span>
            <span className="block sm:inline sm:ml-1">({results.questionResults.filter(q => !q.isCorrect).length})</span>
          </button>
          <button 
            onClick={() => setQuestionFilter('regulations')}
            className={`px-3 sm:px-4 py-3 sm:py-2 rounded text-xs sm:text-sm transition-colors min-h-[44px] sm:min-h-0 touch-manipulation ${
              questionFilter === 'regulations' 
                ? 'bg-blue-600 text-white' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200 active:bg-blue-300'
            }`}
          >
            <span className="block sm:inline">Regulations</span>
            <span className="block sm:inline sm:ml-1">({results.questionResults.filter(q => q.isRegulation).length})</span>
          </button>
          <button 
            onClick={() => setQuestionFilter('technical')}
            className={`px-3 sm:px-4 py-3 sm:py-2 rounded text-xs sm:text-sm transition-colors min-h-[44px] sm:min-h-0 touch-manipulation ${
              questionFilter === 'technical' 
                ? 'bg-green-600 text-white' 
                : 'bg-green-100 text-green-700 hover:bg-green-200 active:bg-green-300'
            }`}
          >
            <span className="block sm:inline">Technical</span>
            <span className="block sm:inline sm:ml-1">({results.questionResults.filter(q => !q.isRegulation).length})</span>
          </button>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {filteredQuestions.map((question, index) => {
            const isCorrect = question.isCorrect;
            
            return (
              <div key={question.examId || `question-${index}`} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 space-y-2 sm:space-y-0">
                  <h4 className="font-medium text-gray-900 text-sm sm:text-base leading-tight sm:pr-4">
                    <span className="font-semibold">Q{question.examQuestionNumber}:</span> {question.question}
                  </h4>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      question.isRegulation 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {question.isRegulation ? 'Regulations' : 'Technical'}
                    </span>
                    <span className="text-lg">
                      {isCorrect ? '✅' : '❌'}
                    </span>
                  </div>
                </div>
                
                {!isCorrect && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-sm space-y-2">
                    <div>
                      <span className="text-red-800 font-medium">Your answer:</span> 
                      <span className="ml-1 break-words">{question.options[question.userAnswer] || 'Not answered'}</span>
                    </div>
                    <div>
                      <span className="text-red-800 font-medium">Correct answer:</span> 
                      <span className="ml-1 break-words">{question.options[question.correct]}</span>
                    </div>
                    {question.explanation && (
                      <div>
                        <span className="text-red-800 font-medium">Explanation:</span> 
                        <span className="ml-1 break-words">{question.explanation}</span>
                      </div>
                    )}
                    <div className="text-xs text-red-600 pt-1 border-t border-red-200">
                      <span className="font-medium">Source:</span> <span className="break-words">{question.sourceTitle}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4">
          <button
            onClick={onRetakeExam}
            className="w-full sm:w-auto bg-blue-600 text-white px-6 py-4 sm:py-3 rounded-md hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium text-base min-h-[48px] touch-manipulation"
          >
            Take Another Mock Exam
          </button>
          <Link
            to="/"
            className="w-full sm:w-auto inline-flex justify-center items-center bg-gray-600 text-white px-6 py-4 sm:py-3 rounded-md hover:bg-gray-700 active:bg-gray-800 transition-colors font-medium text-base min-h-[48px] touch-manipulation"
          >
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MockExamResults;